// Progressive enhancement only. Adds always use the audited theme ProductForm.
if (!customElements.get('strides-cart-rail')) {
  customElements.define('strides-cart-rail', class extends HTMLElement {
    connectedCallback() {
      this._frame = requestAnimationFrame(() => this.initialize());
    }
    initialize() {
      if (!this.isConnected) return;
      if (this._controller) return;
      const rail = this.querySelector('.cart-upsell__list');
      const previous = this.querySelector('[data-rail-previous]');
      const next = this.querySelector('[data-rail-next]');
      if (!rail || !previous || !next) {
        if (!this._childrenObserver) {
          this._childrenObserver = new MutationObserver(() => this.initialize());
          this._childrenObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
      this._childrenObserver?.disconnect();
      this._childrenObserver = null;
      this._controller = new AbortController();
      const options = { signal: this._controller.signal };
      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        previous.hidden = next.hidden = max < 2;
        previous.disabled = rail.scrollLeft <= 1;
        next.disabled = rail.scrollLeft >= max - 1;
      };
      const move = (direction) => rail.scrollBy({
        left: direction * ((rail.firstElementChild?.getBoundingClientRect().width || rail.clientWidth) + 10),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      previous.addEventListener('click', () => move(-1), options);
      next.addEventListener('click', () => move(1), options);
      rail.addEventListener('scroll', update, { ...options, passive: true });
      rail.addEventListener('keydown', (event) => {
        if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') rail.scrollTo({ left: event.key === 'Home' ? 0 : rail.scrollWidth, behavior: 'instant' });
        else move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this._resize = new ResizeObserver(update);
      this._resize.observe(rail);
      update();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this._frame);
      this._controller?.abort();
      this._resize?.disconnect();
      this._childrenObserver?.disconnect();
      this._childrenObserver = null;
      this._controller = null;
    }
  });
}

// Cart-only interaction. All mutations remain with the unchanged native ProductForm.
if (!customElements.get('gap-family-picker')) {
  customElements.define('gap-family-picker', class extends HTMLElement {
    connectedCallback() {
      this._readyObserver = new MutationObserver(() => this.initialize());
      this._readyObserver.observe(this, {childList:true, subtree:true});
      this.initialize();
    }
    initialize() {
      if (this._events || !this.querySelector('[data-gap-status]') || !this.querySelector('[data-gap-submit]')) return;
      this._readyObserver.disconnect();
      this._events = new AbortController();
      const options = {signal:this._events.signal};
      // Track actual input, rather than blurring modal focus or disabling keyboard
      // outlines. Safari may retain :focus-visible across touch-driven focus().
      this.addEventListener('pointerdown',()=>{this.dataset.gapPointerFocus='';},{...options,capture:true});
      window.addEventListener('keydown',()=>{delete this.dataset.gapPointerFocus;},{...options,capture:true});
      // Theme FocusTrap listens at document capture. Intercept only our active modal
      // at window capture, before the enclosing drawer can deactivate on Escape.
      window.addEventListener('keydown',event=>{
        if(event.key!=='Escape'||!this._dialog?.open||!this.closest('cart-drawer')?.hasAttribute('open'))return;
        event.preventDefault();event.stopImmediatePropagation();this.closePopover();
      },{...options,capture:true});
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || button.disabled) return;
        if (button.matches('[data-gap-close]')) { this.closePopover(); return; }
        if (this.busy()) return;
        if (button.matches('[data-gap-alternative]')) {
          this.openSwap(button);
        } else if (button.matches('[data-gap-add]')) {
          const product=button.closest('[data-gap-product]');
          if (product.hidden) return;
          if (product.querySelector('[data-gap-choice-required]')) this.openPopover(product);
          else this.submitChoice(product);
        } else if (button.matches('[data-gap-swap-choice]')) {
          const products=[...this.querySelectorAll('[data-gap-product]')];
          const next=products.find(p=>p.dataset.productId===button.dataset.choiceProduct);
          if(!next)return;
          const previousManual=this._manualSwapProductId;
          this._manualSwapProductId=next.dataset.productId;
          if(![...next.querySelectorAll('[data-gap-variant]')].some(n=>this.valid(n,this.closest('[data-gap-widget]')))){this._manualSwapProductId=previousManual;return;}
          products.forEach(p=>{p.hidden=true;this.resetChoice(p);});next.hidden=false;
          this.querySelector('[data-gap-status]').textContent=next.dataset.productTitle;
          this.closePopover();
        } else if (button.matches('[data-gap-size]')) {
          const product=button.closest('[data-gap-product]');
          if (product.hidden || !this.valid(button,this.closest('[data-gap-widget]'))) return;
          product.querySelector('[data-gap-id]').value=button.dataset.gapVariant;
          this.updateChoice(product);
          this.closePopover(false);
          this.submitChoice(product);
        }
      },options);
      this.addEventListener('submit',event=>this.beforeSubmit(event),{...options,capture:true});
      this.querySelectorAll('[data-gap-popover]').forEach(dialog=>{
        dialog.addEventListener('cancel',event=>{event.preventDefault();this.closePopover();},options);
        dialog.addEventListener('close',()=>{
          if(dialog.open)return;
          (dialog.closest('[data-gap-product]')?.querySelector('[data-gap-add]')||this.querySelector('[data-gap-alternative]'))?.setAttribute('aria-expanded','false');
          if(this._dialog===dialog)this._dialog=null;
        },options);
        dialog.addEventListener('pointerdown',event=>{
          if(event.target!==dialog)return;
          const b=dialog.getBoundingClientRect();
          if(event.clientX<b.left||event.clientX>b.right||event.clientY<b.top||event.clientY>b.bottom){event.preventDefault();this.closePopover();}
        },options);
        dialog.addEventListener('keydown',event=>{
          if(event.key==='Tab') {
            const focusable=[...dialog.querySelectorAll('button:not(:disabled)')];
            const first=focusable[0],last=focusable[focusable.length-1];
            if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}
            else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}
            return;
          }
          if(!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)||!event.target.matches('[data-gap-size], [data-gap-swap-choice]'))return;
          const buttons=[...dialog.querySelectorAll('[data-gap-size]:not(:disabled), [data-gap-swap-choice]:not(:disabled)')];
          const current=buttons.indexOf(event.target);
          const index=event.key==='Home'?0:event.key==='End'?buttons.length-1:(current+(['ArrowLeft','ArrowUp'].includes(event.key)?-1:1)+buttons.length)%buttons.length;
          event.preventDefault();buttons[index]?.focus({preventScroll:true});
          buttons[index]?.scrollIntoView({block:'nearest',inline:'nearest'});
        },options);
      });
      document.documentElement.addEventListener('theme:loading:end',()=>this.updateLocks(),options);
      window.addEventListener('resize',()=>this.positionPopover(),options);
      window.visualViewport?.addEventListener('resize',()=>this.positionPopover(),options);
      this.closest('strides-cart-rail')?.querySelector('ul')?.addEventListener('scroll',()=>{const rail=this.closest('strides-cart-rail')?.querySelector('ul');if(this._dialog&&Math.abs((rail?.scrollLeft||0)-(this._openRailScroll||0))>1)this.closePopover();},{...options,passive:true});
      this.querySelectorAll('[data-gap-product]').forEach(p=>this.resetChoice(p));
      window.StridesShippingCart?.schedule();
    }
    busy() { return this._checking || [...this.querySelectorAll('form')].some(f=>f._stridesCartSubmitting); }
    variant(product) {
      const input=product.querySelector('[data-gap-id]');
      if(!input?.value)return null;
      return [...product.querySelectorAll('[data-gap-variant]')].find(n=>n.dataset.gapVariant===input.value)||null;
    }
    valid(node,root) {
      if(!node||!root||node.dataset.eligible!=='true')return false;
      const price=Number(node.dataset.price),gap=Number(root.dataset.gap),threshold=Number(root.dataset.threshold);
      const product=node.closest('[data-gap-product]');
      const explicitAlternative=product?.dataset.gapManualAlternative==='true'&&this._manualSwapProductId===product.dataset.productId;
      const expandedSeed=product?.dataset.gapExpandedSeed==='true'&&product.dataset.gapManualAlternative!=='true';
      return window.StridesShippingCart?.qualifies(node,root) === true && /^\d+$/.test(node.dataset.gapVariant||'')&&Number.isFinite(price)&&price>0&&price>=gap&&gap>0&&(price*100<=threshold*25||explicitAlternative||expandedSeed);
    }
    resetChoice(product) {
      if(product.querySelector('[data-gap-choice-required]'))product.querySelector('[data-gap-id]').value='';
      product.querySelector('[data-gap-error]').hidden=true;
      this.updateChoice(product);
    }
    updateChoice(product) {
      const root=this.closest('[data-gap-widget]'),node=this.variant(product),valid=this.valid(node,root),busy=this.busy();
      const choose=Boolean(product.querySelector('[data-gap-choice-required]'));
      const supported=typeof product.querySelector('dialog')?.showModal==='function';
      const canChoose=choose&&supported&&[...product.querySelectorAll('[data-gap-size]')].some(n=>this.valid(n,root));
      const button=product.querySelector('[data-gap-add]');
      button.disabled=busy||!(choose?canChoose:valid);
      button.setAttribute('aria-busy',busy?'true':'false');
      product.querySelector('[data-gap-submit]').disabled=busy||!valid;
      const display=node||product.querySelector('[data-gap-default]');
      const from=product.querySelector('[data-gap-from]');
      from.hidden=Boolean(node)||display.dataset.pricesVary!=='true';
      const price=product.querySelector('[data-gap-price]'),compare=product.querySelector('[data-gap-compare]');
      price.hidden=!from.hidden;compare.hidden=!(Number(display.dataset.compare)>Number(display.dataset.price));
      price.textContent=display.dataset.money;
      price.classList.toggle('cart-upsell__price--sale',!compare.hidden);
      from.classList.toggle('cart-upsell__price--sale',!compare.hidden);
      product.querySelector('[data-gap-compare-value]').textContent=display.dataset.compareMoney;
      button.setAttribute('aria-label',this.dataset.add+': '+product.dataset.productTitle+(node?.dataset.label?', '+node.dataset.label:''));
    }
    updateLocks() {
      const busy=this.busy();
      this.querySelectorAll('[data-gap-alternative]').forEach(n=>{n.disabled=busy;});
      this.querySelectorAll('[data-gap-size]').forEach(n=>{n.disabled=busy||!this.valid(n,this.closest('[data-gap-widget]'));});
      this.querySelectorAll('[data-gap-product]').forEach(p=>this.updateChoice(p));
    }
    openSwap(button) {
      if(this.busy())return;
      this.closePopover(false);
      const dialog=this.querySelector('[data-gap-swap-dialog]');
      if(typeof dialog?.showModal!=='function')return;
      const current=this.querySelector('[data-gap-product]:not([hidden])')?.dataset.productId;
      dialog.querySelectorAll('[data-gap-swap-choice]').forEach(n=>n.setAttribute('aria-current',n.dataset.choiceProduct===current?'true':'false'));
      this._dialog=dialog;this._opener=button;
      this._openRailScroll=this.closest('strides-cart-rail')?.querySelector('ul')?.scrollLeft||0;dialog.showModal();button.setAttribute('aria-expanded','true');this.positionPopover();
      dialog.querySelector('[data-gap-swap-choice]')?.focus({preventScroll:true});
    }
    openPopover(product) {
      if(this.busy()||product.hidden)return;
      this.closePopover(false);this.resetChoice(product);
      const dialog=product.querySelector('[data-gap-popover]');
      if(typeof dialog?.showModal!=='function')return;
      this._dialog=dialog;this._opener=product.querySelector('[data-gap-add]');
      this._openRailScroll=this.closest('strides-cart-rail')?.querySelector('ul')?.scrollLeft||0;dialog.showModal();this._opener.setAttribute('aria-expanded','true');
      this.positionPopover();
      dialog.querySelector('[data-gap-size]:not(:disabled)')?.focus({preventScroll:true});
    }
    positionPopover() {
      const dialog=this._dialog,opener=this._opener;
      if(!dialog?.open||!opener?.isConnected)return;
      const vv=window.visualViewport,top=(vv?.offsetTop||0)+8,left=(vv?.offsetLeft||0)+8;
      const right=left+(vv?.width||innerWidth)-16,bottom=top+(vv?.height||innerHeight)-16;
      const b=opener.getBoundingClientRect(),above=b.top-top-8,below=bottom-b.bottom-8;
      const upward=above>=120||above>=below;
      dialog.style.width=Math.min(300,right-left)+'px';
      dialog.style.maxHeight=Math.max(44,Math.min(320,upward?above:below,bottom-top))+'px';
      const size=dialog.getBoundingClientRect();
      dialog.style.left=Math.max(left,Math.min(b.right-size.width,right-size.width))+'px';
      dialog.style.top=Math.max(top,Math.min(upward?b.top-size.height-8:b.bottom+8,bottom-size.height))+'px';
      dialog.dataset.placement=upward?'above':'below';
    }
    closePopover(restore=true) {
      const dialog=this._dialog,opener=this._opener;
      this._dialog=null;
      if(dialog?.open)dialog.close();
      opener?.setAttribute('aria-expanded','false');
      if(restore&&opener?.isConnected&&!opener.disabled)opener.focus({preventScroll:true});
    }
    submitChoice(product) {
      if(this.busy()||product.hidden||!this.valid(this.variant(product),this.closest('[data-gap-widget]')))return;
      this.updateChoice(product);
      product.querySelector('form').requestSubmit(product.querySelector('[data-gap-submit]'));
    }
    async beforeSubmit(event) {
      const form=event.target;
      if(!form.matches('[data-gap-form]'))return;
      if(this._approvedForm===form){this._approvedForm=null;return;}
      event.preventDefault();event.stopImmediatePropagation();
      if(this.busy())return;
      const product=form.closest('[data-gap-product]'),root=this.closest('[data-gap-widget]'),selected=this.variant(product);
      if(product.hidden||!this.valid(selected,root))return;
      const snapshot={id:selected.dataset.gapVariant,price:selected.dataset.price,compare:selected.dataset.compare,product:product.dataset.productId};
      this._checking=true;this._readAbort=new AbortController();
      const timeout=setTimeout(()=>this._readAbort?.abort(),10000);this.updateLocks();
      try{
        const url=new URL(location.href);
        url.hash='';url.searchParams.delete('section_id');url.searchParams.set('sections',root.dataset.section);
        const response=await fetch(url,{method:'GET',credentials:'same-origin',cache:'no-store',signal:this._readAbort.signal});
        if(!response.ok)throw Error('read');
        const json=await response.json(),text=json[root.dataset.section];
        if(typeof text!=='string')throw Error('section');
        const doc=new DOMParser().parseFromString(text,'text/html');
        const fresh=[...doc.querySelectorAll('[data-gap-widget]')].find(n=>n.dataset.section===root.dataset.section);
        const match=fresh&&[...fresh.querySelectorAll('[data-gap-product]')].find(n=>n.dataset.productId===snapshot.product);
        const variant=match&&[...match.querySelectorAll('[data-gap-variant]')].find(n=>n.dataset.gapVariant===snapshot.id);
        const sameContext=fresh&&['currency','country','threshold','total','locale','sizeContext'].every(k=>fresh.dataset[k]===root.dataset[k]);
        if(!this.isConnected||product.hidden||!sameContext||!this.valid(variant,fresh)||variant.dataset.price!==snapshot.price||variant.dataset.compare!==snapshot.compare||this.variant(product)?.dataset.gapVariant!==snapshot.id)throw Error('stale');
        this._checking=false;this.updateLocks();this._approvedForm=form;
        form.requestSubmit(form.querySelector('[data-gap-submit]'));this._approvedForm=null;
      }catch(error){
        if(this.isConnected){
          root.dataset.gapQuoteInvalid='true';root.hidden=true;
          root.querySelectorAll('gap-family-picker').forEach(picker=>{
            picker.closePopover(false);picker.updateLocks();
          });
          const message=product.querySelector('[data-gap-error]');message.textContent=this.dataset.changed;message.hidden=false;
        }
      }finally{
        clearTimeout(timeout);this._checking=false;this._readAbort=null;
        if(this.isConnected)this.updateLocks();
      }
    }
    disconnectedCallback() {
      this.closePopover(false);this._readAbort?.abort();this._events?.abort();this._readyObserver?.disconnect();
      this._events=null;this._approvedForm=null;
    }
  });
}


if (!window.StridesShippingCart) {
  const signature = cart => JSON.stringify(cart.items.map(item => [item.key, item.quantity, item.final_line_price]));
  const snapshot = () => {
    const state = window.StridesBundleDiscounts?.state;
    const result = state?.lastResult;
    const cart = result?.cart;
    if (!state || state.pending || state.reloadPending || !['verified','noop','skipped'].includes(result?.status) ||
        !cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) return null;
    return cart;
  };
  const qualifies = (node, root) => {
    const runtime = window.StridesBundleDiscounts, cart = snapshot();
    if (!node || !root || root.dataset.gapQuoteInvalid === 'true' || runtime?.roleGuardVersion !== 1 || typeof runtime.quoteAddition !== 'function' || !cart) return false;
    if (cart.currency !== root.dataset.currency || cart.total_price !== Number(root.dataset.total) ||
        signature(cart) !== root.dataset.cartLines) return false;
    const product = node.closest('[data-gap-product]');
    if (!product) return false;
    let quote;
    try { quote = runtime.quoteAddition({productId: product.dataset.productId, variantId: node.dataset.gapVariant,
      price: Number(node.dataset.price), quantity: 1, currency: cart.currency}); } catch { return false; }
    return quote?.status === 'estimated' && quote.currency === cart.currency &&
      quote.cartToken === cart.token && quote.revision === runtime.state.revision &&
      quote.beforeTotal === cart.total_price && Number.isSafeInteger(quote.afterTotal) &&
      quote.incrementalTotal === quote.afterTotal - quote.beforeTotal &&
      Number.isSafeInteger(quote.afterTotalLowerBound) && quote.afterTotalLowerBound <= quote.afterTotal &&
      quote.afterTotalLowerBound >= Number(root.dataset.threshold);
  };
  const refresh = () => {
    const cart = snapshot();
    document.querySelectorAll('[data-shipping-threshold]').forEach(bar => {
      if (!cart) { bar.hidden = true; return; }
      if (cart.currency !== bar.dataset.shippingCurrency || cart.requires_shipping === false ||
          !cart.items.some(item=>item.requires_shipping)) { bar.hidden = true; return; }
      const threshold = Number(bar.dataset.shippingThreshold), money = window.StridesGeld;
      if (!(threshold > 0) || !money) { bar.hidden = true; return; }
      const remaining = Math.max(0, threshold-cart.total_price), progress = Math.min(100, cart.total_price*100/threshold);
      const text = bar.querySelector('.strides-free-shipping__text'), track = bar.querySelector('[role="progressbar"]'), fill = bar.querySelector('.strides-free-shipping__fill');
      if (!text || !track || !fill) return;
      const pattern = money.parseMoneyPattern(bar.dataset.moneyZero,bar.dataset.moneyThousand);
      text.textContent = (remaining ? bar.dataset.shippingPrefix + money.formatMoney(remaining,pattern) + bar.dataset.shippingSuffix : bar.dataset.shippingReached) + '!';
      track.setAttribute('aria-valuenow',String(Math.round(progress)));fill.style.width=progress+'%';bar.hidden=false;
    });
    document.querySelectorAll('[data-gap-quote-required]').forEach(root => {
      const pickers = [...root.querySelectorAll('gap-family-picker')];
      pickers.forEach(picker => {
        if (!picker._events) { picker.closest('[data-gap-family]').hidden=true; return; }
        const products=[...picker.querySelectorAll('[data-gap-product]')];
        const validProduct=p=>[...p.querySelectorAll('[data-gap-variant]')].some(n=>picker.valid(n,root));
        const current=products.find(p=>!p.hidden);
        const next=current && validProduct(current) ? current : products.find(validProduct);
        if (current !== next) {
          picker.closePopover(false);
          products.forEach(p=>{p.hidden=p!==next;});
        }
        picker.closest('[data-gap-family]').hidden=!next;
        picker.querySelectorAll('[data-gap-swap-choice]').forEach(button=>{
          const product=products.find(p=>p.dataset.productId===button.dataset.choiceProduct);
          const previousManual=picker._manualSwapProductId;
          if(product)picker._manualSwapProductId=product.dataset.productId;
          button.disabled=!product || !validProduct(product);
          picker._manualSwapProductId=previousManual;
        });
        picker.updateLocks();
      });
      root.hidden=!pickers.some(p=>p._events && !p.closest('[data-gap-family]').hidden);
    });
  };
  let scheduled=false;
  const schedule = () => {
    if (scheduled) return;
    scheduled=true;
    queueMicrotask(async()=>{
      try { await window.StridesBundleDiscounts?.idle?.(); } catch {}
      scheduled=false;refresh();
    });
  };
  window.StridesShippingCart={qualifies,schedule};
  document.addEventListener('cart:change',schedule);
  document.addEventListener('theme:loading:end',schedule);
  window.addEventListener('pageshow',schedule);
  schedule();
}
